// Détail d'une demande d'achat : suivi pour le demandeur ; décision et commande pour le responsable achats.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, GlobalStyles, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AddOutlined, CheckOutlined, CloseOutlined, EditOutlined, PrintOutlined, ShoppingCartOutlined } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { achatsAPI, daAPI, depensesAPI } from '../../services/api';
import { apiError, dateFr, dateHeureFr } from '../../utils/format';
import { DA_STATUS } from './status';
import { DepenseDialog } from './Depenses';
import { useAuth } from '../../context/AuthContext';
import { tokens } from '../../theme';

const Ligne = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: { sm: 2 }, py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: 14 }}>{children}</Box>
  </Box>
);

const DemandeAchat = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.info || '');
  const [dlg, setDlg] = useState(null); // { type: 'approuver' | 'refuser' | 'commander', ... }
  const [fournisseurs, setFournisseurs] = useState([]);
  const { canDepenses } = useAuth();
  const [depenses, setDepenses] = useState(null);
  const [depDlg, setDepDlg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { daAPI.get(id).then((r) => setD(r.data)).catch((e) => setError(apiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  const chargerDep = useCallback(() => { if (canDepenses) depensesAPI.list({ demande_id: id }).then((r) => setDepenses(r.data)).catch(() => {}); }, [id, canDepenses]);
  useEffect(() => { chargerDep(); }, [chargerDep]);
  useEffect(() => { if (d?.acheteur) achatsAPI.fournisseurs().then((r) => setFournisseurs(r.data)).catch(() => {}); }, [d?.acheteur]);

  const run = async (fn, after) => {
    setBusy(true); setError('');
    try { const r = await fn(); setDlg(null); if (after) after(r); else { setInfo(r.data.message); load(); } } catch (e) { setError(apiError(e)); setDlg(null); } finally { setBusy(false); }
  };

  if (!d) return <Layout title="Demande d'achat">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const st = DA_STATUS[d.statut];
  const ouverte = ['SOUMISE', 'APPROUVEE'].includes(d.statut);

  return (
    <Layout title={`Demande d'achat ${d.numero}`} plain
      actions={<Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>}>
      {/* Impression : seulement la demande (sans menus ni boutons) */}
      <GlobalStyles styles={{ '@media print': {
        'header, nav, .MuiDrawer-root, .MuiAppBar-root, .no-print, button, .MuiAlert-action': { display: 'none !important' },
        body: { background: '#fff !important' },
      } }} />
      <Box sx={{ display: 'none', '@media print': { display: 'block' }, mb: 2, borderBottom: '2px solid #12284C', pb: 1 }}>
        <b>DOCC SAS</b> — Demande d'achat {d.numero}
      </Box>
      {info && <Alert className="no-print" severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert className="no-print" severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center', '@media print': { display: 'none' } }}>
        <Chip label={st.label} color={st.color} />
        {d.urgence === 'URGENTE' && <Chip label="Urgent" color="error" />}
        <Box sx={{ flex: 1 }} />
        {d.statut === 'SOUMISE' && (d.proprietaire || d.acheteur) && (
          <Button startIcon={<EditOutlined />} onClick={() => navigate(`/demandes-achat/${id}/modifier`)}>Modifier</Button>
        )}
        {ouverte && (d.proprietaire || d.acheteur) && (
          <Button color="error" onClick={() => window.confirm('Annuler cette demande ?') && run(() => daAPI.annuler(id))}>Annuler la demande</Button>
        )}
        {d.acheteur && d.statut === 'SOUMISE' && (
          <>
            <Button variant="outlined" color="error" startIcon={<CloseOutlined />} onClick={() => setDlg({ type: 'refuser', reponse: '' })}>Refuser</Button>
            <Button variant="outlined" color="success" startIcon={<CheckOutlined />} onClick={() => setDlg({ type: 'approuver', reponse: '' })}>Approuver</Button>
          </>
        )}
        {d.acheteur && ouverte && (
          <Button variant="contained" startIcon={<ShoppingCartOutlined />} onClick={() => setDlg({ type: 'commander', fournisseur_id: '', taux_tva: 18 })}>
            Créer le bon de commande
          </Button>
        )}
      </Box>

      {d.statut === 'SOUMISE' && !d.acheteur && <Alert severity="info" sx={{ mb: 2 }}>Demande transmise au responsable des achats. Vous serez prévenu de sa décision.</Alert>}
      {d.statut === 'REFUSEE' && <Alert severity="error" sx={{ mb: 2 }}>Refusée par {d.traite_par_nom} le {dateHeureFr(d.traite_le)}{d.reponse ? ` — ${d.reponse}` : ''}</Alert>}
      {d.statut === 'APPROUVEE' && <Alert severity="info" sx={{ mb: 2 }}>Approuvée par {d.traite_par_nom} le {dateHeureFr(d.traite_le)}{d.reponse ? ` — ${d.reponse}` : ''}. Commande à passer.</Alert>}
      {d.statut === 'COMMANDEE' && (
        <Alert severity="success" sx={{ mb: 2 }}
          action={d.acheteur && d.bc_id ? <Button onClick={() => navigate(`/achats/commandes/${d.bc_id}`)}>Ouvrir le bon de commande</Button> : null}>
          En commande{d.bc_numero ? ` — bon de commande ${d.bc_numero}` : ' — bon de commande en préparation'} (traité par {d.traite_par_nom}).
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Panel title="Demande">
            <Ligne label="Numéro"><b style={{ fontFamily: tokens.mono }}>{d.numero}</b></Ligne>
            <Ligne label="Demandeur">{d.demandeur}</Ligne>
            <Ligne label="Date">{dateHeureFr(d.created_at)}</Ligne>
            <Ligne label="Service / chantier">{d.service || '—'}</Ligne>
            <Ligne label="Besoin pour le">{d.date_besoin ? dateFr(d.date_besoin) : '—'}</Ligne>
            <Ligne label="Urgence">{d.urgence === 'URGENTE' ? <b style={{ color: '#B4232A' }}>Urgente</b> : 'Normale'}</Ligne>
            <Ligne label="Motif"><span style={{ whiteSpace: 'pre-line' }}>{d.motif}</span></Ligne>
          </Panel>
        </Grid>
        <Grid item xs={12} md={7}>
          <Panel title={`Articles (${d.lignes.length})`} flush>
            <Table size="small">
              <TableHead><TableRow><TableCell>Article</TableCell><TableCell align="right">Quantité</TableCell><TableCell>Précision</TableCell></TableRow></TableHead>
              <TableBody>
                {d.lignes.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{l.designation}{l.intrant_code && <Chip size="small" label="stock" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{String(l.quantite).replace('.', ',')} {l.unite}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{l.observation || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
          {canDepenses && (
            <Panel title={`Dépenses réalisées${depenses ? ` (${depenses.nombre})` : ''}`} flush sx={{ mt: 2 }} className="no-print"
              action={<Button size="small" startIcon={<AddOutlined />} onClick={() => setDepDlg({})}>Ajouter une dépense</Button>}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Libellé</TableCell><TableCell>Fournisseur</TableCell><TableCell align="right">Montant</TableCell></TableRow></TableHead>
                <TableBody>
                  {(depenses?.depenses || []).map((x) => (
                    <TableRow key={x.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/depenses')}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{x.date_depense.split('-').reverse().join('/')}</TableCell>
                      <TableCell>{x.libelle}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{x.fournisseur || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{Math.round(x.montant).toLocaleString('fr-FR')} F</TableCell>
                    </TableRow>
                  ))}
                  {depenses && !depenses.depenses.length && <TableRow><TableCell colSpan={4} sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>Aucune dépense enregistrée pour cette demande. Cliquez sur « Ajouter une dépense » pour saisir ce qui a été réellement dépensé.</TableCell></TableRow>}
                  {depenses && depenses.depenses.length > 0 && (
                    <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 } }}>
                      <TableCell colSpan={3}>Total dépensé</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{Math.round(depenses.total).toLocaleString('fr-FR')} F</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Panel>
          )}
          <Box sx={{ display: 'none', '@media print': { display: 'grid' }, gridTemplateColumns: '1fr 1fr', gap: 3, mt: 4 }}>
            <Box sx={{ borderTop: '1px solid #999', pt: 1, height: 90 }}>Demandeur</Box>
            <Box sx={{ borderTop: '1px solid #999', pt: 1, height: 90 }}>Responsable des achats</Box>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={!!dlg} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
        {dlg?.type === 'commander' && (
          <>
            <DialogTitle>Bon de commande à partir de {d.numero}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              <Typography sx={{ fontSize: 14 }}>Un bon de commande brouillon est créé avec les articles de la demande. Vous compléterez ensuite les prix avant de l'envoyer au fournisseur.</Typography>
              <TextField select label="Fournisseur" required value={dlg.fournisseur_id} onChange={(e) => setDlg({ ...dlg, fournisseur_id: e.target.value })}
                helperText={fournisseurs.length ? '' : 'Aucun fournisseur : créez-le dans Achats → Fournisseurs'}>
                {fournisseurs.map((f) => <MenuItem key={f.id} value={f.id}>{f.nom}</MenuItem>)}
              </TextField>
              <TextField label="TVA (%)" type="number" value={dlg.taux_tva} onChange={(e) => setDlg({ ...dlg, taux_tva: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Annuler</Button>
              <Button variant="contained" disabled={!dlg.fournisseur_id || busy}
                onClick={() => run(() => daAPI.commander(id, { fournisseur_id: dlg.fournisseur_id, taux_tva: dlg.taux_tva }),
                  (r) => navigate(`/achats/commandes/${r.data.bc_id}/modifier`))}>Créer le bon de commande</Button>
            </DialogActions>
          </>
        )}
        {(dlg?.type === 'approuver' || dlg?.type === 'refuser') && (
          <>
            <DialogTitle>{dlg.type === 'approuver' ? 'Approuver' : 'Refuser'} la demande {d.numero}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              <TextField fullWidth multiline minRows={2} required={dlg.type === 'refuser'}
                label={dlg.type === 'refuser' ? 'Motif du refus' : 'Commentaire (facultatif)'} value={dlg.reponse}
                onChange={(e) => setDlg({ ...dlg, reponse: e.target.value })} />
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>Le demandeur est prévenu par email s'il a une adresse.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Annuler</Button>
              <Button variant="contained" color={dlg.type === 'refuser' ? 'error' : 'success'} disabled={busy || (dlg.type === 'refuser' && !dlg.reponse.trim())}
                onClick={() => run(() => (dlg.type === 'refuser' ? daAPI.refuser(id, dlg.reponse) : daAPI.approuver(id, dlg.reponse)))}>
                {dlg.type === 'refuser' ? 'Refuser' : 'Approuver'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <DepenseDialog open={!!depDlg} demandeId={Number(id)} onClose={() => setDepDlg(null)} onDone={() => { setDepDlg(null); setInfo('Dépense enregistrée'); chargerDep(); }} />
    </Layout>
  );
};

export default DemandeAchat;
